import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import type { UtilityTemplate } from '@/services/api/utility-templates';

const CONFIRM_WORD = 'EXCLUIR';

interface DeleteSubmissionsDialogProps {
    open: boolean;
    selected: UtilityTemplate[];
    allSubmissions: UtilityTemplate[];
    pageById: Map<string, MetaPage>;
    isDeleting: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Confirmação da exclusão em massa (Meta + banco). Lista cada página que vai
 * sair e o que sobra dela no grupo — o caso comum é a mesma página em dois
 * idiomas depois de trocar o idioma da página.
 */
export default function DeleteSubmissionsDialog({
    open,
    selected,
    allSubmissions,
    pageById,
    isDeleting,
    onCancel,
    onConfirm,
}: DeleteSubmissionsDialogProps) {
    const [confirmText, setConfirmText] = useState('');

    useEffect(() => {
        if (!open) setConfirmText('');
    }, [open]);

    const handleOpenChange = (next: boolean) => {
        if (!next && !isDeleting) onCancel();
    };

    const selectedIds = new Set(selected.map((s) => s.id));
    const remainingLanguages = (s: UtilityTemplate) =>
        allSubmissions
            .filter((o) => o.page_id === s.page_id && !selectedIds.has(o.id))
            .map((o) => o.language);

    const onlyLocalCount = selected.filter((s) => !s.meta_template_id).length;
    // `pageById` só tem as páginas da conta; fora dela o backend não chama a Meta.
    const outsideAccountCount = selected.filter((s) => s.meta_template_id && !pageById.has(s.page_id)).length;
    const canConfirm = confirmText.trim().toUpperCase() === CONFIRM_WORD && !isDeleting;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100 mb-1">
                        <Trash2 className="w-5 h-5 text-red-500 shrink-0" />
                        <DialogTitle className="text-sm font-semibold text-gray-800">
                            Excluir {selected.length} página{selected.length > 1 ? 's' : ''} deste template
                        </DialogTitle>
                    </div>
                </DialogHeader>

                <div className="space-y-3 px-1">
                    <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                        <ul className="space-y-1 leading-snug list-disc pl-3">
                            <li>
                                Apaga o template <strong>na Meta</strong> (só o idioma listado) e <strong>no banco</strong>.
                                Não tem como desfazer.
                            </li>
                            <li>
                                Botões de mensagens já enviadas com estes templates param de redirecionar.
                            </li>
                            {onlyLocalCount > 0 && (
                                <li>
                                    {onlyLocalCount} ainda não {onlyLocalCount > 1 ? 'chegaram' : 'chegou'} na Meta
                                    (rascunho/falha) — sai só do banco.
                                </li>
                            )}
                            {outsideAccountCount > 0 && (
                                <li>
                                    {outsideAccountCount} página{outsideAccountCount > 1 ? 's' : ''} não{' '}
                                    {outsideAccountCount > 1 ? 'estão' : 'está'} mais na sua conta — sai só do banco,
                                    o template fica na Meta.
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className="max-h-[40vh] overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100">
                        {selected.map((s) => {
                            const remaining = remainingLanguages(s);
                            return (
                                <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {pageById.get(s.page_id)?.page_name ?? s.page_id}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            ID: {s.page_id}
                                            <span className="text-gray-300 mx-1">·</span>
                                            <span className="font-semibold text-red-600">{s.language}</span>
                                            <span className="text-gray-300 mx-1">·</span>
                                            {s.status}
                                        </p>
                                    </div>
                                    <span
                                        className={`shrink-0 text-[11px] font-medium ${
                                            remaining.length ? 'text-gray-500' : 'text-red-600'
                                        }`}
                                    >
                                        {remaining.length ? `continua em ${remaining.join(', ')}` : 'sai do template'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-600">
                            Digite <span className="font-mono font-semibold">{CONFIRM_WORD}</span> para confirmar:
                        </label>
                        <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={CONFIRM_WORD}
                            autoFocus
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={isDeleting}>
                        Cancelar
                    </Button>
                    <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={onConfirm}
                        disabled={!canConfirm}
                    >
                        {isDeleting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        Excluir definitivamente
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
